/**
 * UIPlannerModal
 *
 * The chat-based UI Planner. Three-phase flow:
 *   1. Pick workflows from the current workbook (multi-select)
 *   2. Probe selected workflows (server runs them with sample input)
 *   3. Conversational planning with Sonnet → live preview → save
 *
 * Reuses the existing `.planner-*` CSS classes from WorkflowCanvas.css for
 * visual consistency with the WorkflowPlannerModal.
 */
const React = require('react');
const { createElement: h, useEffect, useRef, useState } = React;
const ReactDOM = require('react-dom');
const uiPageStorage = require('../lib/uiPageStorage');
const workbookStorage = require('../lib/workbookStorage');
const UiPageRenderer = require('./UiPageRenderer');
// Multi-select answers are arrays; single ones are strings. Shared with the
// other planner surfaces so there is one definition of "what is selected".
const { selectionList, selectionText } = require('../lib/plannerChatFormat');

var INITIAL_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm your UI Planner. I'll help you build a React + Tailwind page that uses your playbook's workflows as its backend. First, pick the workflows you want this page to use."
};

// ── Token usage helpers ─────────────────────────────────────────────────────

function formatTokenCount(n) {
  if (!n || n <= 0) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
}

// ── Preview-iframe capture helper ───────────────────────────────────────────
//
// Snapshots the live preview iframe directly into a data URL — no browser
// permission popup, no screen picker. Uses the SVG <foreignObject> trick:
// serialize the iframe's <body> into an inline SVG, load that SVG into an
// Image, then draw the Image to a canvas. Same-origin srcdoc iframes are
// fully accessible, so we can read contentDocument.
//
// Critical details that took several iterations to get right:
//   1. The HTML inside <foreignObject> must be VALID XML, not HTML5. We use
//      XMLSerializer (not outerHTML) so void elements self-close, attributes
//      are quoted, and entities are escaped.
//   2. We snapshot getElementsByTagName('*') into a static array BEFORE
//      removing scripts/links from the clone — otherwise the live HTMLCollection
//      shifts indices as we remove nodes and the source/dest pairing skews.
//   3. External assets (scripts, stylesheets, non-data img src) would taint
//      the canvas (CORS), so they're stripped from the clone.
//   4. Tailwind's resolved styles live in the iframe's computed styles. We
//      inline a curated subset onto every element so the snapshot renders
//      faithfully without needing Tailwind to re-process inside the SVG.
function captureIframeToDataUrl(iframe) {
  return new Promise(function(resolve, reject) {
    if (!iframe || !iframe.contentDocument) {
      reject(new Error('Preview iframe is not available.'));
      return;
    }
    var doc = iframe.contentDocument;
    var srcBody = doc.body;
    if (!srcBody) { reject(new Error('Preview iframe has no body.')); return; }

    var width  = iframe.clientWidth  || doc.documentElement.scrollWidth  || 1024;
    var height = iframe.clientHeight || doc.documentElement.scrollHeight || 768;

    // Properties worth inlining — full computedStyle is huge, but these are
    // the visual ones that matter for a faithful snapshot.
    var STYLE_PROPS = [
      'display','position','top','left','right','bottom','width','height',
      'min-width','min-height','max-width','max-height',
      'margin','margin-top','margin-right','margin-bottom','margin-left',
      'padding','padding-top','padding-right','padding-bottom','padding-left',
      'border','border-top','border-right','border-bottom','border-left',
      'border-color','border-style','border-width','border-radius',
      'box-shadow','background','background-color','background-image',
      'background-size','background-position','background-repeat',
      'color','opacity',
      'font-family','font-size','font-weight','font-style',
      'line-height','letter-spacing','text-align','text-transform',
      'text-decoration','white-space','overflow','overflow-x','overflow-y',
      'flex','flex-direction','flex-wrap','justify-content','align-items',
      'align-self','gap',
      'grid-template-columns','grid-template-rows',
      'transform','transform-origin','filter','backdrop-filter','z-index',
      'visibility','cursor','box-sizing'
    ];

    function sanitizeStyleValue(prop, v) {
      if (!v) return '';
      // Transparent default — safe to drop, no visual effect.
      if (v === 'rgba(0, 0, 0, 0)') return '';
      // IMPORTANT: do NOT strip 'none' or 'normal' globally. They are
      // meaningful for several properties — most critically `display: none`,
      // which is how every data-show-when element stays hidden. If we drop
      // it, hidden error banners, result cards, and loading-state spans
      // suddenly appear in the snapshot.
      return v;
    }

    var clone = srcBody.cloneNode(true);

    // Snapshot the element lists BEFORE mutating the clone — getElementsByTagName
    // returns a LIVE collection that re-indexes when we remove nodes.
    var srcElements = Array.prototype.slice.call(srcBody.getElementsByTagName('*'));
    var dstElements = Array.prototype.slice.call(clone.getElementsByTagName('*'));

    // Pass 1: inline computed styles in the same document order. We do this
    // before any removals so source/dest pairing stays aligned.
    var i;
    for (i = 0; i < srcElements.length && i < dstElements.length; i++) {
      var s = srcElements[i];
      var d = dstElements[i];
      if (!s || !d) continue;
      try {
        var cs = doc.defaultView.getComputedStyle(s);
        var inline = '';
        for (var p = 0; p < STYLE_PROPS.length; p++) {
          var prop = STYLE_PROPS[p];
          var v = sanitizeStyleValue(prop, cs.getPropertyValue(prop));
          if (v) inline += prop + ':' + v + ';';
        }
        if (inline) d.setAttribute('style', inline);
      } catch (_e) { /* per-element style failures are non-fatal */ }
    }

    // Pass 2: remove anything that would trigger an external fetch and
    // taint the canvas, or that XML doesn't allow.
    for (i = 0; i < dstElements.length; i++) {
      var el = dstElements[i];
      if (!el || !el.tagName) continue;
      var tag = el.tagName.toUpperCase();
      if (tag === 'SCRIPT' || tag === 'LINK' || tag === 'IFRAME' || tag === 'NOSCRIPT' || tag === 'STYLE') {
        if (el.parentNode) el.parentNode.removeChild(el);
        continue;
      }
      if (tag === 'IMG') {
        var src = el.getAttribute('src') || '';
        if (src && !/^data:/i.test(src)) el.removeAttribute('src');
      }
      // Strip event handler attributes (on*) — XML doesn't care, but they're noise.
      if (el.attributes) {
        var toRemove = [];
        for (var a = 0; a < el.attributes.length; a++) {
          var an = el.attributes[a].name;
          if (an && an.toLowerCase().indexOf('on') === 0) toRemove.push(an);
        }
        for (var r = 0; r < toRemove.length; r++) el.removeAttribute(toRemove[r]);
      }
    }

    // ── Serialize to valid XHTML for SVG foreignObject ────────────────
    // Direct XMLSerializer on an HTML-document body often produces subtly
    // invalid XML (empty xmlns="", unescaped entities in CDN-injected
    // content, etc.). Instead, we grab innerHTML (HTML5), round-trip it
    // through DOMParser as text/html (lenient), then re-serialize from
    // the resulting XHTML-compatible DOM. This lets the browser's HTML
    // parser fix any quirks before we ask XMLSerializer to produce XML.
    var bodyXml;
    try {
      var rawHtml = clone.innerHTML || '';
      // Round-trip through DOMParser's HTML parser — it fixes unclosed tags,
      // normalises entities, and produces a clean DOM we can serialize to XML.
      var tmpDoc = new DOMParser().parseFromString(
        '<!doctype html><html><head></head><body>' + rawHtml + '</body></html>',
        'text/html'
      );
      var tmpBody = tmpDoc.body;
      // Re-serialize via XMLSerializer on the parsed (now clean) body.
      bodyXml = new XMLSerializer().serializeToString(tmpBody);
    } catch (err) {
      reject(new Error('Could not serialize preview body: ' + (err && err.message ? err.message : err)));
      return;
    }

    // Remove empty xmlns="" declarations — Chromium's XMLSerializer emits
    // these on elements in the null namespace, breaking the SVG parser.
    bodyXml = bodyXml.replace(/ xmlns=""/g, '');

    // Ensure the XHTML namespace is declared.
    if (!/\bxmlns\s*=\s*"http:\/\/www\.w3\.org\/1999\/xhtml"/i.test(bodyXml)) {
      bodyXml = bodyXml.replace(/^<body\b/, '<body xmlns="http://www.w3.org/1999/xhtml"');
    }

    // Strip XML-invalid control characters that can sneak in via computed
    // style values or text nodes.
    // eslint-disable-next-line no-control-regex
    bodyXml = bodyXml.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + width + '" height="' + height + '">' +
        '<foreignObject width="100%" height="100%" x="0" y="0">' +
          bodyXml +
        '</foreignObject>' +
      '</svg>';

    var encoded = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    var img = new Image();
    img.onload = function() {
      try {
        var canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        var ctx = canvas.getContext('2d');
        // Paint a white background first so transparent areas don't go black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = function() {
      // Most onerror cases are XML-validity failures. Log the SVG (truncated)
      // so we can debug from devtools.
      try {
        // eslint-disable-next-line no-console
        console.error('[UI Planner] preview snapshot failed; SVG starts with:', svg.slice(0, 400));
      } catch (_e) {}
      reject(new Error('Could not render preview snapshot. Check devtools for the SVG that failed to parse.'));
    };
    img.src = encoded;
  });
}

// ── Image attachment cost helpers ───────────────────────────────────────────
//
// Anthropic charges ~1 vision token per ~750 image pixels. Capping the
// longest edge at 1280px gives a worst-case ~2.2k tokens per attachment
// (~$0.0066 at Sonnet 4.6 input pricing) — well below Anthropic's own
// 1568px auto-resize threshold, so we save bandwidth AND tokens.
//
// Returns a Promise<{ dataUrl, width, height }>. Resizes only when needed;
// when the image already fits, it's returned untouched (no quality loss).
var IMAGE_MAX_EDGE = 1280;

function resizeImageForVision(dataUrl, maxEdge) {
  return new Promise(function(resolve) {
    if (!dataUrl) { resolve(null); return; }
    var img = new Image();
    img.onload = function() {
      var w = img.naturalWidth;
      var h = img.naturalHeight;
      if (!w || !h) { resolve({ dataUrl: dataUrl, width: 0, height: 0 }); return; }
      var longest = Math.max(w, h);
      if (longest <= maxEdge) {
        resolve({ dataUrl: dataUrl, width: w, height: h });
        return;
      }
      var scale = maxEdge / longest;
      var dw = Math.round(w * scale);
      var dh = Math.round(h * scale);
      var canvas = document.createElement('canvas');
      canvas.width = dw;
      canvas.height = dh;
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, dw, dh);
      // PNG keeps the screenshot crisp; JPEG would be smaller but blur text.
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: dw, height: dh });
    };
    img.onerror = function() { resolve({ dataUrl: dataUrl, width: 0, height: 0 }); };
    img.src = dataUrl;
  });
}

// Anthropic's vision token formula: tokens ≈ (width × height) / 750.
function estimateImageTokens(width, height) {
  if (!width || !height) return 0;
  return Math.ceil((width * height) / 750);
}

/**
 * Merge an incoming partial usage payload into the existing tokenUsage state.
 * Probe-stream events carry { probing: ... } and chat-stream events carry
 * { planning: ... } — merging preserves whichever slice the other stream owns.
 */
function mergeTokenUsage(prev, incoming) {
  if (!incoming) return prev;
  var next = Object.assign({}, prev || {});
  if (incoming.planning) next.planning = incoming.planning;
  if (incoming.probing)  next.probing  = incoming.probing;
  if (incoming.generating) next.generating = incoming.generating;
  return next;
}

// ── Light markdown formatting (mirrors WorkflowPlannerModal) ────────────────

function formatText(text) {
  if (!text) return text;
  var lines = String(text).split('\n');
  var result = [];
  lines.forEach(function(line, li) {
    if (li > 0) result.push(h('br', { key: 'br-' + li }));
    var trimmed = line.trimStart();
    var isBullet = trimmed.startsWith('- ');
    var content = isBullet ? trimmed.slice(2) : line;
    var parts = [];
    var pattern = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
    var last = 0;
    var m;
    while ((m = pattern.exec(content)) !== null) {
      if (m.index > last) parts.push(content.slice(last, m.index));
      if (m[2]) parts.push(h('strong', { key: 'b-' + li + '-' + m.index }, m[2]));
      else if (m[3]) parts.push(h('code', { key: 'c-' + li + '-' + m.index, className: 'planner-inline-code' }, m[3]));
      last = m.index + m[0].length;
    }
    if (last < content.length) parts.push(content.slice(last));
    if (isBullet) {
      result.push(h('span', { key: 'li-' + li, className: 'planner-msg-bullet' }, '\u2022\u00a0', parts));
    } else {
      result.push.apply(result, parts);
    }
  });
  return result;
}

// ── Question parsing (same XML format as WorkflowPlannerModal) ──────────────

function parseAiMessage(content) {
  if (!content || typeof content !== 'string') return { parts: [{ type: 'text', text: content || '' }] };
  var parts = [];
  var pattern = /<question\s+([^>]*?)>([\s\S]*?)<\/question>/gi;
  var lastIndex = 0;
  var match;
  while ((match = pattern.exec(content)) !== null) {
    var before = content.slice(lastIndex, match.index).trim();
    if (before) parts.push({ type: 'text', text: before });
    // label="..." plus the optional multi="true" flag (several answers apply).
    var attrs = match[1] || '';
    var labelAttr = attrs.match(/label\s*=\s*"([^"]*)"/i);
    var label = labelAttr ? labelAttr[1].trim() : '';
    var isMulti = /\bmulti\s*=\s*"\s*(?:true|yes|1)\s*"/i.test(attrs);
    var optBlock = match[2];
    var opts = [];
    var optPattern = /<option>([\s\S]*?)<\/option>/gi;
    var om;
    while ((om = optPattern.exec(optBlock)) !== null) {
      var t = om[1].trim();
      if (t) opts.push(t);
    }
    if (opts.length > 0) parts.push({ type: 'question', question: label, options: opts, multi: isMulti });
    else parts.push({ type: 'input', question: label });
    lastIndex = match.index + match[0].length;
  }
  var rest = content.slice(lastIndex).trim();
  if (rest) parts.push({ type: 'text', text: rest });
  if (parts.length === 0) parts.push({ type: 'text', text: content });

  return { parts: parts };
}

// ── Workflow picker panel ───────────────────────────────────────────────────

function WorkflowPickerPanel(props) {
  var workflows = props.workflows || [];
  var selectedIds = props.selectedIds || [];
  var onToggle = props.onToggle;
  var disabled = props.disabled;

  if (!workflows.length) {
    return h('div', { className: 'planner-preview-empty' },
      h('div', { className: 'planner-preview-empty-title' }, 'No workflows in this playbook'),
      h('div', { className: 'planner-preview-empty-desc' },
        'Create at least one workflow before designing a UI page.')
    );
  }

  return h('div', { style: { display: 'flex', flexDirection: 'column', height: '100%' } },
    h('div', { className: 'planner-plan-header' },
      h('div', { className: 'planner-plan-title' }, 'Pick workflows to use'),
      h('div', { className: 'planner-plan-count' },
        selectedIds.length + ' selected')
    ),
    h('div', { style: { flex: 1, overflowY: 'auto', padding: '8px 12px' } },
      workflows.map(function(wf) {
        var checked = selectedIds.indexOf(wf.id) !== -1;
        return h('label', {
          key: wf.id,
          style: {
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            padding: '10px 12px', marginBottom: '6px',
            border: '1px solid ' + (checked ? '#3b82f6' : '#e2e8f0'),
            borderRadius: '8px', cursor: 'pointer',
            background: checked ? '#eff6ff' : '#ffffff'
          }
        },
          h('input', {
            type: 'checkbox',
            checked: checked,
            onChange: function() { onToggle(wf.id); },
            disabled: disabled,
            style: { marginTop: '3px' }
          }),
          h('div', { style: { flex: 1, minWidth: 0 } },
            h('div', { style: { fontWeight: 600, fontSize: '13px', color: '#0f172a' } }, wf.name || wf.id),
            h('div', { style: { fontSize: '11px', color: '#64748b', marginTop: '2px' } }, wf.id)
          )
        );
      })
    ),
    h('div', { style: { padding: '12px', borderTop: '1px solid #e2e8f0' } },
      h('div', { style: { fontSize: '11px', color: '#64748b', textAlign: 'center' } },
        selectedIds.length
          ? 'Describe what you want in the chat — the planner will probe these workflows automatically.'
          : 'Tick at least one workflow to use as the page backend.')
    )
  );
}

// ── Probe progress panel ────────────────────────────────────────────────────

function ProbeProgressPanel(props) {
  var probeEvents = props.probeEvents || [];
  return h('div', { style: { padding: '12px', overflowY: 'auto', height: '100%' } },
    h('div', { className: 'planner-plan-title', style: { marginBottom: '12px' } }, 'Probing workflows...'),
    probeEvents.map(function(pe, idx) {
      var icon = pe.status === 'running' ? '\u23f3'
        : pe.status === 'ok' ? '\u2705' : '\u274c';
      return h('div', {
        key: idx,
        style: {
          padding: '10px 12px', marginBottom: '8px',
          background: pe.status === 'error' ? '#fef2f2' : '#f8fafc',
          border: '1px solid ' + (pe.status === 'error' ? '#fecaca' : '#e2e8f0'),
          borderRadius: '6px'
        }
      },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
          h('span', null, icon),
          h('span', { style: { fontWeight: 600, fontSize: '13px' } }, pe.workflowId)
        ),
        pe.capability ? h('div', { style: { marginTop: '6px', fontSize: '12px', color: '#475569' } }, pe.capability) : null,
        pe.error ? h('div', { style: { marginTop: '6px', fontSize: '12px', color: '#dc2626' } }, pe.error) : null,
        pe.outputShape !== undefined
          ? h('div', { style: { marginTop: '6px', fontSize: '11px', color: '#64748b', fontFamily: 'monospace' } },
              JSON.stringify(pe.outputShape))
          : null,
        pe.probeLogs
          ? h('details', { style: { marginTop: '6px' } },
              h('summary', { style: { fontSize: '11px', color: '#64748b', cursor: 'pointer', userSelect: 'none' } },
                'Probe logs'),
              h('pre', {
                style: {
                  margin: '6px 0 0 0', padding: '8px 10px',
                  background: '#f1f5f9', color: '#334155',
                  borderRadius: '4px', fontSize: '11px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  overflowX: 'auto', whiteSpace: 'pre-wrap',
                  maxHeight: '160px', overflowY: 'auto'
                }
              }, pe.probeLogs)
            )
          : null
      );
    })
  );
}

// ── Crop overlay for the preview snapshot ───────────────────────────────────

/**
 * Modal overlay that displays a freshly-snapshotted preview image and lets
 * the user drag a rectangle over the part they want to attach. Confirming
 * crops at NATURAL image resolution (so no quality loss) and resolves the
 * cropped PNG data URL via `onConfirm`. If no rectangle is drawn, the full
 * snapshot is attached.
 */
function CropOverlay(props) {
  var imageDataUrl = props.imageDataUrl;
  var onConfirm = props.onConfirm || function() {};
  var onCancel = props.onCancel || function() {};

  var [rect, setRect] = useState(null); // { x, y, w, h } in displayed-pixel coords
  var [dragging, setDragging] = useState(false);
  var imgRef = useRef(null);
  var dragStartRef = useRef(null);

  function getRelativePoint(e) {
    var img = imgRef.current;
    if (!img) return { x: 0, y: 0 };
    var box = img.getBoundingClientRect();
    var clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0] && e.touches[0].clientX) || 0;
    var clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0] && e.touches[0].clientY) || 0;
    return {
      x: Math.max(0, Math.min(box.width,  clientX - box.left)),
      y: Math.max(0, Math.min(box.height, clientY - box.top))
    };
  }

  function onMouseDown(e) {
    e.preventDefault();
    var p = getRelativePoint(e);
    dragStartRef.current = p;
    setDragging(true);
    setRect({ x: p.x, y: p.y, w: 0, h: 0 });
  }

  function onMouseMove(e) {
    if (!dragging || !dragStartRef.current) return;
    var p = getRelativePoint(e);
    var s = dragStartRef.current;
    setRect({
      x: Math.min(s.x, p.x),
      y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x),
      h: Math.abs(p.y - s.y)
    });
  }

  function onMouseUp() { setDragging(false); }

  function clearCrop() {
    setRect(null);
    dragStartRef.current = null;
  }

  function confirmCrop() {
    var img = imgRef.current;
    if (!img || !rect || rect.w < 4 || rect.h < 4) {
      // No (or trivial) rectangle drawn — attach the whole snapshot.
      onConfirm(imageDataUrl);
      return;
    }
    var box = img.getBoundingClientRect();
    var scaleX = img.naturalWidth  / box.width;
    var scaleY = img.naturalHeight / box.height;
    var sx = Math.round(rect.x * scaleX);
    var sy = Math.round(rect.y * scaleY);
    var sw = Math.round(rect.w * scaleX);
    var sh = Math.round(rect.h * scaleY);
    if (sw < 1 || sh < 1) { onConfirm(imageDataUrl); return; }

    // Draw to an offscreen canvas at NATURAL resolution.
    var canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    var ctx = canvas.getContext('2d');
    var src = new Image();
    src.onload = function() {
      ctx.drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
      onConfirm(canvas.toDataURL('image/png'));
    };
    src.onerror = function() { onConfirm(imageDataUrl); };
    src.src = imageDataUrl;
  }

  return h('div', {
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(15,23,42,0.92)',
      zIndex: 200,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px'
    },
    onMouseUp: onMouseUp,
    onMouseLeave: onMouseUp
  },
    h('div', {
      style: {
        color: '#e2e8f0', marginBottom: '12px',
        fontSize: '13px', fontWeight: 500, textAlign: 'center'
      }
    }, rect && rect.w > 4
      ? 'Click "Attach crop" to use this region — or drag again to redraw.'
      : 'Drag a rectangle over the part of the preview you want to attach.'),

    h('div', {
      style: {
        position: 'relative',
        maxWidth: '90vw', maxHeight: '70vh',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        borderRadius: '8px', overflow: 'hidden',
        cursor: 'crosshair', userSelect: 'none',
        background: '#ffffff'
      },
      onMouseDown: onMouseDown,
      onMouseMove: onMouseMove
    },
      h('img', {
        ref: imgRef,
        src: imageDataUrl,
        alt: 'preview snapshot',
        draggable: false,
        style: {
          display: 'block',
          maxWidth: '90vw', maxHeight: '70vh',
          width: 'auto', height: 'auto',
          pointerEvents: 'none'
        }
      }),
      rect ? h('div', {
        style: {
          position: 'absolute',
          left:   rect.x + 'px',
          top:    rect.y + 'px',
          width:  rect.w + 'px',
          height: rect.h + 'px',
          border: '2px solid #38bdf8',
          background: 'rgba(56,189,248,0.15)',
          boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)',
          pointerEvents: 'none'
        }
      }) : null
    ),

    h('div', { style: { marginTop: '16px', display: 'flex', gap: '10px' } },
      h('button', {
        type: 'button',
        onClick: onCancel,
        style: {
          padding: '8px 18px', background: 'transparent', color: '#e2e8f0',
          border: '1px solid #475569', borderRadius: '8px',
          fontSize: '13px', cursor: 'pointer'
        }
      }, 'Cancel'),
      rect && rect.w > 4 ? h('button', {
        type: 'button',
        onClick: clearCrop,
        style: {
          padding: '8px 18px', background: 'transparent', color: '#e2e8f0',
          border: '1px solid #475569', borderRadius: '8px',
          fontSize: '13px', cursor: 'pointer'
        }
      }, 'Redraw') : null,
      h('button', {
        type: 'button',
        onClick: confirmCrop,
        style: {
          padding: '8px 18px', background: '#0ea5e9', color: '#ffffff',
          border: 'none', borderRadius: '8px',
          fontSize: '13px', fontWeight: 600, cursor: 'pointer'
        }
      }, rect && rect.w > 4 ? 'Attach crop' : 'Attach full snapshot')
    )
  );
}

// ── Live preview panel ──────────────────────────────────────────────────────

function PreviewPanel(props) {
  var uiPage = props.uiPage;
  if (!uiPage || !uiPage.html) {
    return h('div', { className: 'planner-preview-empty' },
      h('div', { className: 'planner-preview-empty-title' }, 'Preview'),
      h('div', { className: 'planner-preview-empty-desc' },
        'Your UI page will appear here once the planner generates it.')
    );
  }
  return h('div', { style: { height: '100%', background: '#ffffff' } },
    h(UiPageRenderer, { value: uiPage })
  );
}

// ── Main modal ──────────────────────────────────────────────────────────────

function UIPlannerModal(props) {
  var onClose = props.onClose || function() {};
  var onSavePage = props.onSavePage || function() {};
  var initialWorkflows = props.workflows || [];
  var workbookId = props.workbookId || '';

  // When the planner is opened FROM an existing page (the editor's "Open Planner"
  // button), `existingPage` is the loaded page record:
  //   { id, name, value: { html, workflowRefs }, ... }
  // The modal then skips the workflow picker, primes the preview with the
  // current HTML, auto-probes the page's workflowRefs, and tells the chat
  // backend to refine the existing HTML instead of generating from scratch.
  var existingPage = props.existingPage || null;
  var existingPageHtml = existingPage && existingPage.value && existingPage.value.html
    ? String(existingPage.value.html)
    : '';

  // If no workflows are passed but we have a workbookId, fetch them.
  var [workflows, setWorkflows] = useState(initialWorkflows);
  useEffect(function() {
    if (initialWorkflows && initialWorkflows.length) {
      setWorkflows(initialWorkflows);
      return;
    }
    if (workbookId) {
      workbookStorage.loadWorkbook(workbookId).then(function(wb) {
        if (wb && Array.isArray(wb.workflows)) setWorkflows(wb.workflows);
      }).catch(function() {});
    }
  }, [workbookId, initialWorkflows]);

  // When there's exactly one workflow available (e.g. opened from a standalone
  // workflow editor, or a workbook with a single flow), the picker UI is just
  // friction — there's nothing to choose. Auto-select it and hide the picker.
  var singleWorkflowMode = !existingPage && workflows.length === 1;

  // Phase: 'pick' | 'probing' | 'chat'
  // Probing is no longer triggered by a manual button — it kicks off
  // automatically when the user sends their first chat message in 'pick'.
  // When opening on an existing page, we skip 'pick' and start in 'probing'
  // (auto-probe runs from a useEffect below).
  var focusWorkflowId = props.focusWorkflowId || '';
  var primedIds = (existingPage && existingPage.value && Array.isArray(existingPage.value.workflowRefs))
    ? existingPage.value.workflowRefs.slice()
    : (focusWorkflowId ? [focusWorkflowId]
      : (singleWorkflowMode ? [workflows[0].id] : []));
  // When opening on an existing page the preview is already primed from the
  // saved HTML, so jump straight into 'chat' — probing runs invisibly in the
  // background (see the auto-probe useEffect below).
  var [phase, setPhase] = useState(existingPage ? 'chat' : 'pick');
  var [selectedIds, setSelectedIds] = useState(primedIds);

  // If workflows hydrate asynchronously to a single entry, sync the auto-pick.
  useEffect(function() {
    if (existingPage) return;
    if (workflows.length === 1 && selectedIds.length === 0) {
      setSelectedIds([workflows[0].id]);
    }
  }, [workflows, existingPage]);
  var [capabilities, setCapabilities] = useState([]);
  var [probeEvents, setProbeEvents] = useState([]);

  var focusedFlow = focusWorkflowId && workflows.find(function(w) { return w && w.id === focusWorkflowId; });
  var SINGLE_FLOW_MESSAGE = {
    role: 'assistant',
    content: "Hi! I'm your UI Planner. I'll help you build a React + Tailwind page powered by **" + ((focusedFlow && focusedFlow.name) || (workflows[0] && workflows[0].name) || 'your workflow') + "**. Just describe the UI you want and I'll generate it."
  };
  var EDIT_INITIAL_MESSAGE = {
    role: 'assistant',
    content: existingPage
      ? "I've loaded **" + (existingPage.name || 'your page') + "** into the preview on the right. Tell me what you'd like to change — copy, layout, colors, the workflow wiring — and I'll apply it on top of the current page."
      : INITIAL_MESSAGE.content
  };
  var [messages, setMessages] = useState([
    existingPage
      ? EDIT_INITIAL_MESSAGE
      : ((singleWorkflowMode || focusWorkflowId) ? SINGLE_FLOW_MESSAGE : INITIAL_MESSAGE)
  ]);
  var [input, setInput] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [streamingText, setStreamingText] = useState('');
  var [thinkingStatus, setThinkingStatus] = useState('');
  var [tokenUsage, setTokenUsage] = useState(null);
  // When editing an existing page, prime the preview with the current HTML so
  // the user sees their page on the right immediately, before any AI work.
  var [uiPage, setUiPageRaw] = useState(existingPage ? existingPage.value : null);
  var uiPageHistoryRef = useRef(existingPage && existingPage.value ? [existingPage.value] : []);
  var [uiPageHistoryPos, setUiPageHistoryPos] = useState(0);
  function setUiPage(next) {
    if (!next) { setUiPageRaw(next); return; }
    var hist = uiPageHistoryRef.current;
    uiPageHistoryRef.current = hist.slice(0, uiPageHistoryPos + 1).concat([next]);
    var newPos = uiPageHistoryRef.current.length - 1;
    setUiPageHistoryPos(newPos);
    setUiPageRaw(next);
  }
  function undoUiPage() {
    if (uiPageHistoryPos <= 0) return;
    var newPos = uiPageHistoryPos - 1;
    setUiPageHistoryPos(newPos);
    setUiPageRaw(uiPageHistoryRef.current[newPos]);
  }
  function redoUiPage() {
    if (uiPageHistoryPos >= uiPageHistoryRef.current.length - 1) return;
    var newPos = uiPageHistoryPos + 1;
    setUiPageHistoryPos(newPos);
    setUiPageRaw(uiPageHistoryRef.current[newPos]);
  }
  var canUndo = uiPageHistoryRef.current.length > 1 && uiPageHistoryPos > 0;
  var canRedo = uiPageHistoryPos < uiPageHistoryRef.current.length - 1;
  var [saving, setSaving] = useState(false);
  var [minimized, setMinimized] = useState(false);
  // True while a probe stream is in flight (initial pick-phase probe, the
  // background auto-probe on existing pages, or a mid-conversation re-probe).
  // Lets the input stay enabled while still gating actual chat sends until
  // capabilities are ready.
  var [probing, setProbing] = useState(false);
  // Messages the user typed while a probe/stream was in flight. Drained
  // FIFO once both `loading` and `probing` are false.
  var [queuedMessages, setQueuedMessages] = useState([]);

  // Per-question selections
  var [selections, setSelections] = useState({});

  // Holds context for probe after user fills in workflow start inputs.
  // { messages, attachment, selection, schemas, selectedIds }

  // ── Preview snapshot attachment ────────────────────────────────────────
  // Click "Snapshot preview" → captureIframeToDataUrl runs (no popup) →
  // CropOverlay opens with the snapshot → user drags a rectangle (or skips)
  // → confirmed crop is downscaled and stored as the attachment chip.
  // The Sonnet planner has vision, so the AI can analyze whatever region
  // the user picks.
  //
  //   captureRaw  = full snapshot data URL (only set while CropOverlay is open)
  //   attachment  = { dataUrl, width, height, tokens } ready for send
  //                 (downscaled to IMAGE_MAX_EDGE for cost efficiency)
  //   captureBusy = true while the snapshot pipeline is running
  var [captureRaw, setCaptureRaw] = useState(null);
  var [attachment, setAttachment] = useState(null);
  var [captureBusy, setCaptureBusy] = useState(false);

  function handleScreenCapture() {
    if (captureBusy) return;
    if (!uiPage || !uiPage.html) {
      setError('No preview to capture yet.');
      return;
    }
    // Find the preview iframe inside this modal — UiPageRenderer renders
    // exactly one iframe in the right-hand panel.
    var iframe = document.querySelector('.planner-modal .planner-preview-panel iframe');
    if (!iframe) {
      setError('Preview iframe not found.');
      return;
    }
    setCaptureBusy(true);
    setError('');
    captureIframeToDataUrl(iframe)
      .then(function(rawDataUrl) {
        if (!rawDataUrl) throw new Error('Snapshot was empty.');
        // Open the crop overlay so the user can pick a region.
        setCaptureRaw(rawDataUrl);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Capture failed');
      })
      .then(function() { setCaptureBusy(false); });
  }

  function handleCropConfirm(croppedDataUrl) {
    setCaptureRaw(null);
    if (!croppedDataUrl) { setAttachment(null); return; }
    // Downscale for cost efficiency BEFORE storing — every byte we attach
    // becomes input tokens charged at 6 µ-credits each on Sonnet 4.6.
    resizeImageForVision(croppedDataUrl, IMAGE_MAX_EDGE).then(function(result) {
      if (!result || !result.dataUrl) { setAttachment(null); return; }
      setAttachment({
        dataUrl: result.dataUrl,
        width:   result.width,
        height:  result.height,
        tokens:  estimateImageTokens(result.width, result.height)
      });
    }).catch(function() {
      setError('Could not process the cropped image.');
    });
  }

  function handleCropCancel() {
    setCaptureRaw(null);
  }

  function handleAttachmentRemove() {
    setAttachment(null);
  }

  // ── UI Select: pick a single element to refine ─────────────────────────
  // Click "UI select" → the preview iframe enters select mode → hovering an
  // element highlights it → clicking captures its outerHTML, tags it with a
  // captures the element's outerHTML verbatim, and stores it as a selection
  // chip. On the next chat send, the backend's single-element refinement flow
  // asks the AI to emit ONLY the changed element (huge token savings) and
  // splices the result in via a literal string replace on the source HTML.
  //
  //   selectMode = boolean: are we currently in click-to-pick mode
  //   selection  = { snippet, label } or null
  // The snippet IS the search key — no `data-uip-target` injection — so the
  // server can do `existingHtml.replace(snippet, updated)` and stay byte-exact.
  var [selectMode, setSelectMode] = useState(false);
  var [selection, setSelection] = useState(null);

  function handleSelectionRemove() {
    setSelection(null);
  }

  function getPreviewIframe() {
    return document.querySelector('.planner-modal .planner-preview-panel iframe');
  }

  // Build a short human label for the selection chip from an element.
  function describeElement(el) {
    if (!el) return 'element';
    var tag = (el.tagName || 'element').toLowerCase();
    var cls = (el.getAttribute && el.getAttribute('class')) || '';
    var first = cls.split(/\s+/).filter(Boolean).slice(0, 2).join(' ');
    return first ? tag + '.' + first.replace(/\s+/g, '.') : tag;
  }

  // Effect: when selectMode is on, attach hover/click listeners and inject
  // a highlight stylesheet into the preview iframe. Cleans up on toggle off.
  useEffect(function() {
    if (!selectMode) return;
    var iframe = getPreviewIframe();
    if (!iframe || !iframe.contentDocument) {
      setSelectMode(false);
      setError('Preview iframe not available.');
      return;
    }
    var doc = iframe.contentDocument;

    // Inject a tiny stylesheet for the hover highlight + cursor.
    var styleEl = doc.createElement('style');
    styleEl.setAttribute('data-uip-select-style', '');
    styleEl.textContent =
      'html, body { cursor: crosshair !important; }' +
      '[data-uip-hover] { outline: 2px solid #0ea5e9 !important; outline-offset: 2px !important; cursor: crosshair !important; }';
    doc.head.appendChild(styleEl);

    var hovered = null;
    function clearHover() {
      if (hovered && hovered.removeAttribute) hovered.removeAttribute('data-uip-hover');
      hovered = null;
    }
    function onMove(e) {
      var el = e.target;
      if (!el || el === doc.documentElement || el === doc.body) { clearHover(); return; }
      if (el === hovered) return;
      clearHover();
      hovered = el;
      if (el.setAttribute) el.setAttribute('data-uip-hover', '');
    }
    function onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      var el = e.target;
      if (!el || el === doc.documentElement || el === doc.body) return;
      el.removeAttribute('data-uip-hover');

      // Capture the element's outerHTML as-is. This becomes the literal
      // search key for the server-side string replace, so we must NOT
      // mutate the live element first (no data-uip-target, no marker
      // attributes — anything we add here would make the snippet diverge
      // from what's in uiPage.html and the replace would miss).
      var snippet = el.outerHTML;
      var label = describeElement(el);

      setSelection({ snippet: snippet, label: label });
      setSelectMode(false);
    }
    function onLeave() { clearHover(); }
    function onKey(e) {
      if (e.key === 'Escape') { setSelectMode(false); }
    }

    doc.addEventListener('mousemove', onMove, true);
    doc.addEventListener('click',     onClick, true);
    doc.addEventListener('mouseleave', onLeave, true);
    doc.addEventListener('keydown',    onKey,   true);
    document.addEventListener('keydown', onKey, true);

    return function cleanup() {
      doc.removeEventListener('mousemove', onMove, true);
      doc.removeEventListener('click',     onClick, true);
      doc.removeEventListener('mouseleave', onLeave, true);
      doc.removeEventListener('keydown',    onKey,   true);
      document.removeEventListener('keydown', onKey, true);
      clearHover();
      var injected = doc.querySelector('style[data-uip-select-style]');
      if (injected && injected.parentNode) injected.parentNode.removeChild(injected);
    };
  }, [selectMode]);

  function handleToggleSelectMode() {
    if (!uiPage || !uiPage.html) {
      setError('Generate a preview first.');
      return;
    }
    setSelectMode(function(prev) { return !prev; });
    setError('');
  }

  // @-mention workflow suggestions in the chat input. When the user types
  // "@" the dropdown opens; subsequent characters filter, ↑/↓ navigates,
  // Enter/Tab selects, Escape dismisses, and any whitespace/end-of-input
  // closes the picker.
  //   start  = caret index of the "@" character (or -1 if closed)
  //   query  = characters typed after "@"
  //   highlight = currently-highlighted suggestion index
  var [mention, setMention] = useState({ start: -1, query: '', highlight: 0 });
  var mentionOpen = mention.start !== -1;
  var mentionFiltered = mentionOpen
    ? workflows.filter(function(wf) {
        var name = String((wf && wf.name) || '').toLowerCase();
        var id   = String((wf && wf.id)   || '').toLowerCase();
        var q    = mention.query.toLowerCase();
        if (!q) return true;
        return name.indexOf(q) !== -1 || id.indexOf(q) !== -1;
      }).slice(0, 8)
    : [];

  function closeMention() {
    setMention({ start: -1, query: '', highlight: 0 });
  }

  function applyMention(wf) {
    if (!wf || mention.start === -1) return;
    var name = String(wf.name || wf.id || '').replace(/\s+/g, '_').trim();
    if (!name) { closeMention(); return; }
    var before = input.slice(0, mention.start);
    var after  = input.slice(mention.start + 1 + mention.query.length);
    // Insert "@workflow_name " (spaces → underscores, trailing space to keep typing).
    var inserted = '@' + name + ' ';
    var nextValue = before + inserted + after;
    setInput(nextValue);
    // Auto-select the mentioned workflow so it gets probed/sent to the planner.
    if (wf.id) {
      setSelectedIds(function(prev) {
        return prev.indexOf(wf.id) === -1 ? prev.concat([wf.id]) : prev;
      });
    }
    closeMention();
    // Restore caret right after the inserted token.
    var nextCaret = (before + inserted).length;
    setTimeout(function() {
      var el = inputRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCaret, nextCaret); } catch (e) {}
    }, 0);
  }

  // Recompute the mention state from the textarea's current value + caret.
  // Called from the input's onChange + onKeyUp so the popover stays in sync
  // even when the user clicks elsewhere or pastes text.
  function refreshMentionFromTextarea(value, caret) {
    if (caret == null) caret = value.length;
    // Walk back from the caret looking for an "@" that begins a fresh token.
    // A valid "@" is either at index 0 or preceded by whitespace, and the
    // characters between it and the caret must contain no whitespace.
    var i = caret - 1;
    var hasNonWord = false;
    while (i >= 0) {
      var ch = value.charAt(i);
      if (ch === '@') {
        var prev = i === 0 ? '' : value.charAt(i - 1);
        if (i === 0 || /\s/.test(prev)) {
          var query = value.slice(i + 1, caret);
          // Reject queries containing whitespace — that means the user has
          // moved past the mention token already.
          if (/\s/.test(query)) { closeMention(); return; }
          setMention(function(prev) {
            return {
              start: i,
              query: query,
              highlight: prev.start === i ? Math.min(prev.highlight, 7) : 0
            };
          });
          return;
        }
      }
      if (/\s/.test(ch)) { hasNonWord = true; break; }
      i -= 1;
    }
    if (mentionOpen) closeMention();
  }

  var chatSessionIdRef = useRef(Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));
  var messagesEndRef = useRef(null);
  var inputRef = useRef(null);
  var inputContainerRef = useRef(null);
  var abortStreamRef = useRef(null);
  var abortProbeRef = useRef(null);

  useEffect(function() {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, selections, streamingText, probeEvents]);

  useEffect(function() {
    if (!loading && phase !== 'probing' && inputRef.current) inputRef.current.focus();
  }, [loading, phase]);

  // Drain queued messages once the probe + stream pipeline is idle. Restores
  // the queued payload into the composer state and re-invokes sendMessage on
  // the next tick so it reads the freshly-set input/attachment/selection.
  useEffect(function() {
    if (loading || probing) return;
    if (!queuedMessages.length) return;
    var next = queuedMessages[0];
    setQueuedMessages(function(prev) { return prev.slice(1); });
    setInput(next.text || '');
    if (next.attachment) setAttachment(next.attachment);
    if (next.selection) setSelection(next.selection);
    var t = setTimeout(function() { sendMessage(); }, 0);
    return function() { clearTimeout(t); };
  }, [loading, probing, queuedMessages]);

  useEffect(function() {
    function onKey(e) {
      if (e.key === 'Escape' && !loading && !minimized) onClose();
    }
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [onClose, loading, minimized]);

  // Cleanup any in-flight streams on unmount
  useEffect(function() {
    return function() {
      if (abortStreamRef.current) abortStreamRef.current();
      if (abortProbeRef.current) abortProbeRef.current();
    };
  }, []);

  // When opened on an existing page, auto-probe with default values so the
  // planner has a capability sheet ready. We no longer ask the user for input
  // values up front — they can provide real inputs on-demand via the chat if
  // needed. This removes the friction of filling in a form on every open.
  useEffect(function() {
    if (!existingPage) return;
    if (!primedIds.length) return;
    setThinkingStatus('Probing workflows...');
    uiPageStorage.fetchWorkflowSchemas(primedIds).then(function(schemas) {
      // Build default inputs from schema defaults
      var userInputs = {};
      schemas.forEach(function(s) {
        if (s.schema && Array.isArray(s.schema.params)) {
          s.schema.params.forEach(function(p) {
            if (p.name) {
              if (!userInputs[s.id]) userInputs[s.id] = {};
              var def = p.defaultValue;
              userInputs[s.id][p.name] = (def !== undefined && def !== null && def !== '') ? def : '';
            }
          });
        }
      });
      startProbing(primedIds, Object.keys(userInputs).length ? userInputs : undefined).then(function(caps) {
        var probeMsg = buildProbeResultsMessage(caps);
        setMessages(function(prev) { return prev.concat([probeMsg]); });
        setThinkingStatus('');
      }).catch(function() {
        setThinkingStatus('');
      });
    }).catch(function() {
      // Schema fetch failed — probe without inputs (best effort)
      startProbing(primedIds).then(function(caps) {
        var probeMsg = buildProbeResultsMessage(caps);
        setMessages(function(prev) { return prev.concat([probeMsg]); });
        setThinkingStatus('');
      }).catch(function() {
        setThinkingStatus('');
      });
    });
    // Intentional empty deps: this should run exactly once when the modal
    // mounts on an existing page.
  }, []);

  function toggleSelection(id) {
    setSelectedIds(function(prev) {
      if (prev.indexOf(id) !== -1) return prev.filter(function(x) { return x !== id; });
      return prev.concat([id]);
    });
  }

  /**
   * Probe the given workflow IDs. Returns a Promise that resolves with the
   * full capabilities array when probing finishes (or rejects on error).
   *
   * Probing used to flip the modal into a dedicated 'probing' phase that took
   * over the right panel — that meant the user clicked Send and then stared at
   * a blocking progress screen for several seconds. Now we keep the chat/preview
   * panel in place and only surface progress as a status string in the chat.
   * Callers control the phase themselves.
   */
  /** Build a collapsible probe-results message for the chat. */
  function buildProbeResultsMessage(caps) {
    var lines = [];
    (caps || []).forEach(function(c) {
      if (!c) return;
      var name = c.name || c.id || 'Workflow';
      var status = c.status === 'ok' ? '\u2705' : '\u274c';
      lines.push(status + ' **' + name + '**');
      if (c.status === 'error') {
        lines.push('Error: ' + (c.error || 'unknown'));
        return;
      }
      if (c.sampleInput !== undefined && c.sampleInput !== null) {
        var inputStr = typeof c.sampleInput === 'string' ? c.sampleInput : JSON.stringify(c.sampleInput, null, 2);
        lines.push('**Input sent:**\n```json\n' + inputStr + '\n```');
      }
      if (c.sampleOutput !== undefined && c.sampleOutput !== null) {
        var outputStr = typeof c.sampleOutput === 'string' ? c.sampleOutput : JSON.stringify(c.sampleOutput, null, 2);
        // Truncate very large outputs for display
        if (outputStr.length > 2000) outputStr = outputStr.slice(0, 2000) + '\n...truncated...';
        lines.push('**Output received:**\n```json\n' + outputStr + '\n```');
      } else {
        lines.push('**Output:** _(no output)_');
      }
    });
    return {
      role: 'assistant',
      content: lines.join('\n\n'),
      displayContent: lines.join('\n\n'),
      probeResults: true
    };
  }

  function startProbing(ids, userInputs) {
    var workflowIds = (ids || []).filter(Boolean);
    if (!workflowIds.length) return Promise.resolve([]);
    setProbeEvents([]);
    setError('');
    setProbing(true);

    return new Promise(function(resolve, reject) {
      function settle(fn, arg) { setProbing(false); fn(arg); }
      var collected = {};
      var abort = uiPageStorage.streamProbeWorkflows(workflowIds, {
        onEvent: function(evt) {
          if (evt.type === 'probes_start') {
            setProbeEvents(evt.workflowIds.map(function(id) {
              return { workflowId: id, status: 'pending' };
            }));
          } else if (evt.type === 'probe_running') {
            setProbeEvents(function(prev) {
              return prev.map(function(pe) {
                if (pe.workflowId === evt.workflowId) return Object.assign({}, pe, { status: 'running' });
                return pe;
              });
            });
          } else if (evt.type === 'probe_result') {
            var full = evt.result || {};
            collected[evt.workflowId] = full;
            setProbeEvents(function(prev) {
              return prev.map(function(pe) {
                if (pe.workflowId === evt.workflowId) {
                  return Object.assign({}, pe, {
                    status: full.status,
                    capability: full.capability,
                    outputShape: full.outputShape,
                    error: full.error,
                    probeLogs: full.probeLogs
                  });
                }
                return pe;
              });
            });
          } else if (evt.type === 'token_usage') {
            setTokenUsage(function(prev) { return mergeTokenUsage(prev, evt.usage); });
          } else if (evt.type === 'done') {
            // Server already emits a done event with the capability list, but
            // we trust per-workflow `result` payloads as the source of truth.
            var capList = workflowIds.map(function(id) { return collected[id]; }).filter(Boolean);
            setCapabilities(capList);
            settle(resolve, capList);
          } else if (evt.type === 'error') {
            setError(evt.message || 'Probe failed');
            settle(reject, new Error(evt.message || 'Probe failed'));
          }
        },
        onError: function(err) {
          setError(err && err.message ? err.message : 'Probe failed');
          settle(reject, err || new Error('Probe failed'));
        }
      }, userInputs ? { userInputs: userInputs } : undefined);
      abortProbeRef.current = abort;
    });
  }

  /**
   * Stream a planner chat turn for an explicit messages array. Used by both
   * doSend (regular send) and sendMessage's first-turn probe-then-send flow.
   *
   * `pendingAttachment` (optional) — a screenshot data URL to attach to the
   * latest user message. Sent as a multipart content block to the planner so
   * Sonnet's vision can read it. The caller should clear its own attachment
   * state after this returns so the same image isn't sent again.
   * `pendingSelection`  (optional) — { id, snippet } when the user picked a
   * single element to refine via "UI select". Triggers the token-saving
   * single-element refinement flow on the backend.
   */
  function streamFromMessages(msgs, caps, pendingAttachment, pendingSelection) {
    setLoading(true);
    setError('');
    setStreamingText('');
    setThinkingStatus('');
    setSelections({});

    var apiMessages = msgs.filter(function(m) {
      return m !== INITIAL_MESSAGE && !m.probeResults;
    }).map(function(m) {
      return { role: m.role, content: m.content };
    });

    if (abortStreamRef.current) abortStreamRef.current();

    // The "existing HTML" sent to the planner is whichever HTML is currently
    // live in the preview: the original page on the very first turn, then the
    // AI's most recent revision on every subsequent turn. This way each turn
    // refines the latest version the user sees, not the stale original.
    var currentHtmlForRefinement = '';
    if (uiPage && uiPage.html) currentHtmlForRefinement = String(uiPage.html);
    else if (existingPageHtml) currentHtmlForRefinement = existingPageHtml;

    var abort = uiPageStorage.streamChatUiPlan(apiMessages, caps, {
      onEvent: function(evt) {
        if (evt.type === 'thinking') setThinkingStatus(evt.text || 'Thinking...');
        else if (evt.type === 'chunk_start') { setThinkingStatus(''); setStreamingText(''); }
        else if (evt.type === 'chunk') setStreamingText(evt.fullText || '');
        else if (evt.type === 'token_usage') {
          setTokenUsage(function(prev) { return mergeTokenUsage(prev, evt.usage); });
        }
        else if (evt.type === 'reprobe_start') {
          // Planner asked to re-probe one or more workflows mid-conversation
          // because the existing capability sheet wasn't enough. Show a clear
          // status string so the user understands what's happening.
          var ids = Array.isArray(evt.workflowIds) ? evt.workflowIds.join(', ') : '';
          setThinkingStatus('Re-probing ' + ids + ' (round ' + (evt.round || 1) + ')...');
          setStreamingText('');
        }
        else if (evt.type === 'reprobe_complete') {
          // Refresh the local capability list with whatever the re-probe
          // returned so subsequent turns from the user start with the latest
          // data, not the stale pre-conversation snapshot.
          var freshResults = Array.isArray(evt.results) ? evt.results : [];
          if (freshResults.length) {
            setCapabilities(function(prev) {
              var next = (prev || []).slice();
              freshResults.forEach(function(r) {
                if (!r || !r.id) return;
                var idx = -1;
                for (var i = 0; i < next.length; i++) {
                  if (next[i] && next[i].id === r.id) { idx = i; break; }
                }
                if (idx >= 0) next[idx] = r;
                else next.push(r);
              });
              return next;
            });
          }
          setThinkingStatus('Continuing with fresh probe data...');
        }
        else if (evt.type === 'done') {
          var assistantMessage = {
            role: 'assistant',
            content: evt.rawMessage || evt.message || '',
            displayContent: evt.message || ''
          };
          setMessages(function(prev) { return prev.concat([assistantMessage]); });
          setStreamingText('');
          setThinkingStatus('');
          setLoading(false);
          abortStreamRef.current = null;
          if (evt.complete && evt.uiPage) {
            setUiPage(evt.uiPage);
          }
          // After a successful single-element splice, the original snippet
          // is no longer in the source HTML — it's been replaced by the new
          // one. Refresh the sticky selection chip to point at the NEW
          // snippet so the next refinement turn finds it.
          if (evt.updatedSelectionSnippet) {
            setSelection(function(prev) {
              if (!prev) return prev;
              return Object.assign({}, prev, { snippet: evt.updatedSelectionSnippet });
            });
          }
        } else if (evt.type === 'error') {
          setLoading(false);
          setStreamingText('');
          setThinkingStatus('');
          abortStreamRef.current = null;
          setError(evt.message || 'Generation failed');
        }
      },
      onError: function(err) {
        setLoading(false);
        setStreamingText('');
        setThinkingStatus('');
        abortStreamRef.current = null;
        setError(err && err.message ? err.message : 'Generation failed');
      }
    }, {
      existingHtml: currentHtmlForRefinement,
      attachment: pendingAttachment || null,
      selectedElement: pendingSelection || null,
      chatSessionId: chatSessionIdRef.current
    });
    abortStreamRef.current = abort;
  }

  function doSend(text) {
    if ((!text && !attachment && !selection) || loading) return;
    var pendingAttachment = attachment;        // { dataUrl, width, height, tokens }
    var pendingDataUrl = pendingAttachment ? pendingAttachment.dataUrl : null;
    var pendingSelection = selection;          // { id, snippet, label }
    // The user message is rendered in the chat history with the attachment
    // thumbnail inline (see renderUserMessage). The attachment URL itself is
    // stashed on the message so the conversation log shows what was sent.
    var fallbackText = text || (pendingSelection ? 'Refine the selected element' : '(screenshot)');
    var newUserMsg = {
      role: 'user',
      content: fallbackText,
      attachment: pendingDataUrl,
      selectionLabel: pendingSelection ? pendingSelection.label : null
    };
    var nextMessages = messages.concat([newUserMsg]);
    setMessages(nextMessages);
    setInput('');
    setAttachment(null);
    // NOTE: selection is intentionally NOT cleared here. Once the user picks
    // an element, the chip stays attached across follow-up messages so every
    // turn refines just that element (huge token savings). The user clears it
    // explicitly via the X button on the chip, or by picking another element.

    streamFromMessages(nextMessages, capabilities, pendingDataUrl, pendingSelection);
  }

  function sendMessage() {
    var text = input.trim();
    if (!text && !attachment && !selection) return;
    // Busy with a probe or an in-flight chat stream — queue the message and
    // clear the composer so the user can keep typing. The drain effect below
    // re-fires sendMessage once both flags clear, in FIFO order.
    if (loading || probing) {
      setQueuedMessages(function(prev) {
        return prev.concat([{ text: text, attachment: attachment, selection: selection }]);
      });
      setInput('');
      setAttachment(null);
      return;
    }
    // Extract @-mentioned workflows from text and auto-select them so they
    // get probed and sent to the planner backend.
    if (text && workflows.length) {
      var mentionedIds = [];
      workflows.forEach(function(wf) {
        if (!wf || !wf.id) return;
        var name = String(wf.name || '').replace(/\s+/g, '_').trim();
        if (name && text.indexOf('@' + name) !== -1) {
          mentionedIds.push(wf.id);
        }
      });
      if (mentionedIds.length) {
        var merged = selectedIds.slice();
        mentionedIds.forEach(function(id) {
          if (merged.indexOf(id) === -1) merged.push(id);
        });
        if (merged.length !== selectedIds.length) {
          selectedIds = merged;
          setSelectedIds(merged);
        }
      }
    }
    // First message in the pick phase: fetch workflow schemas and auto-probe
    // with default values. No longer asks the user for input values up front —
    // they can provide real inputs on-demand via the chat if the defaults
    // aren't sufficient.
    if (phase === 'pick') {
      if (!selectedIds.length) {
        setError('Pick at least one workflow before sending.');
        return;
      }
      var pendingAttachment = attachment;
      var pendingDataUrl = pendingAttachment ? pendingAttachment.dataUrl : null;
      var pendingSelection = selection;
      var fallbackText = text || (pendingSelection ? 'Refine the selected element' : '(screenshot)');
      var firstMsg = {
        role: 'user',
        content: fallbackText,
        attachment: pendingDataUrl,
        selectionLabel: pendingSelection ? pendingSelection.label : null
      };
      var nextMessages = messages.concat([firstMsg]);
      setMessages(nextMessages);
      setInput('');
      setAttachment(null);
      setError('');
      setPhase('chat');
      setLoading(true);
      setThinkingStatus('Probing workflows...');

      // Fetch schemas to extract default values, then probe automatically.
      uiPageStorage.fetchWorkflowSchemas(selectedIds).then(function(schemas) {
        // Build default inputs from schema defaults
        var userInputs = {};
        schemas.forEach(function(s) {
          if (s.schema && Array.isArray(s.schema.params)) {
            s.schema.params.forEach(function(p) {
              if (p.name) {
                if (!userInputs[s.id]) userInputs[s.id] = {};
                var def = p.defaultValue;
                userInputs[s.id][p.name] = (def !== undefined && def !== null && def !== '') ? def : '';
              }
            });
          }
        });

        startProbing(selectedIds, Object.keys(userInputs).length ? userInputs : undefined).then(function(caps) {
          var probeMsg = buildProbeResultsMessage(caps);
          setMessages(function(prev) { return prev.concat([probeMsg]); });
          streamFromMessages(nextMessages, caps, pendingDataUrl, pendingSelection);
        }).catch(function() {
          setLoading(false);
          setThinkingStatus('');
        });
      }).catch(function() {
        // Schema fetch failed — probe without inputs (best effort)
        setThinkingStatus('Probing workflows...');
        startProbing(selectedIds).then(function(caps) {
          var probeMsg = buildProbeResultsMessage(caps);
          setMessages(function(prev) { return prev.concat([probeMsg]); });
          streamFromMessages(nextMessages, caps, pendingDataUrl, pendingSelection);
        }).catch(function() {
          setLoading(false);
          setThinkingStatus('');
        });
      });
      return;
    }
    doSend(text);
  }

  function submitSelections(questions) {
    var answerParts = [];
    for (var k = 0; k < questions.length; k++) {
      var q = questions[k];
      // Multi-select answers arrive as an array — selectionText joins them.
      var ans = selectionText(selections[k]);
      if (ans) answerParts.push(q.question + ': ' + ans);
    }
    if (!answerParts.length) return;
    doSend(answerParts.join('\n'));
  }

  function onKeyDown(e) {
    // When the @-mention picker is open, the keyboard drives the picker
    // first — only fall through to the regular send/Enter behavior when the
    // picker is closed.
    if (mentionOpen && mentionFiltered.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMention(function(prev) {
          return Object.assign({}, prev, {
            highlight: (prev.highlight + 1) % mentionFiltered.length
          });
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMention(function(prev) {
          return Object.assign({}, prev, {
            highlight: (prev.highlight - 1 + mentionFiltered.length) % mentionFiltered.length
          });
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyMention(mentionFiltered[mention.highlight] || mentionFiltered[0]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMention();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function onInputChange(e) {
    var value = e.target.value;
    setInput(value);
    var caret = e.target.selectionStart;
    refreshMentionFromTextarea(value, caret);
  }

  function onInputKeyUp(e) {
    // Catch caret movements (arrow keys, mouse clicks before this fires) so
    // the mention state stays accurate when the user navigates inside text.
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
      var el = e.target;
      refreshMentionFromTextarea(el.value, el.selectionStart);
    }
  }

  function handleSavePage() {
    if (!uiPage) return;
    setSaving(true);
    Promise.resolve(onSavePage(uiPage)).then(function() {
      setSaving(false);
      // Show confirmation message in chat
      var action = existingPage ? 'updated' : 'saved';
      var pageName = (uiPage && uiPage.name) || (existingPage && existingPage.name) || 'Your page';
      var confirmMsg = {
        role: 'assistant',
        content: '**' + pageName + '** has been ' + action + ' successfully.',
        displayContent: '**' + pageName + '** has been ' + action + ' successfully.',
        confirmation: true
      };
      setMessages(function(prev) { return prev.concat([confirmMsg]); });
    }).catch(function(err) {
      setSaving(false);
      setError(err && err.message ? err.message : 'Save failed');
    });
  }

  // ── Render AI message with optional interactive options ───────────────────
  function renderAiMessage(msg, idx, isLast) {
    // Confirmation messages: render as a compact success banner
    if (msg.confirmation) {
      return h('div', { key: idx, className: 'planner-message planner-message-ai' },
        h('div', { className: 'planner-message-content' },
          h('div', {
            style: {
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '10px', fontSize: '13px', color: '#166534'
            }
          },
            h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: '#16a34a', strokeWidth: 2.5 },
              h('path', { d: 'M20 6L9 17l-5-5' })
            ),
            h('span', null, formatText(msg.displayContent || msg.content || ''))
          )
        )
      );
    }
    // Probe results: render as a collapsible details block
    if (msg.probeResults) {
      return h('div', { key: idx, className: 'planner-message planner-message-ai' },
        h('div', { className: 'planner-message-content' },
          h('div', { className: 'planner-message-sender' }, 'UI Planner'),
          h('div', { className: 'planner-message-bubble' },
            h('details', {
              style: { cursor: 'pointer' }
            },
              h('summary', {
                style: {
                  fontSize: '13px', fontWeight: 600, color: '#475569',
                  userSelect: 'none', padding: '2px 0'
                }
              }, '\u2699\ufe0f Probe Results (click to expand)'),
              h('div', {
                style: {
                  marginTop: '8px', fontSize: '12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap', color: '#334155',
                  maxHeight: '400px', overflowY: 'auto',
                  background: '#f8fafc', borderRadius: '6px',
                  padding: '10px', border: '1px solid #e2e8f0'
                }
              }, formatText(msg.displayContent || msg.content || ''))
            )
          )
        )
      );
    }

    // displayContent may be an empty string after stripping <ui_page>/<question>
    // tags — use it verbatim when defined, do NOT fall back to the raw content
    // (which would re-leak the JSON we just stripped).
    var displayText = (msg.displayContent !== undefined && msg.displayContent !== null)
      ? msg.displayContent
      : (msg.content || '');
    if (!String(displayText).trim() && msg.content) {
      // The whole message was just a <ui_page> block — show as confirmation banner.
      return h('div', { key: idx, className: 'planner-message planner-message-ai' },
        h('div', { className: 'planner-message-content' },
          h('div', {
            style: {
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '10px', fontSize: '13px', color: '#166534'
            }
          },
            h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: '#16a34a', strokeWidth: 2.5 },
              h('path', { d: 'M20 6L9 17l-5-5' })
            ),
            'Your page is ready in the preview on the right.'
          )
        )
      );
    }
    var parsed = parseAiMessage(displayText);
    var questions = parsed.parts.filter(function(p) { return p.type === 'question' || p.type === 'input'; });
    var hasQuestions = questions.length > 0;
    var interactive = isLast && hasQuestions && !loading;

    var qIdx = 0;
    var elements = [];
    parsed.parts.forEach(function(part, pIdx) {
      if (part.type === 'text') {
        elements.push(h('div', { key: 'txt-' + pIdx, className: 'planner-message-text' }, formatText(part.text)));
      } else if (part.type === 'question') {
        var thisQIdx = qIdx++;
        // multi="true" → tick as many as apply and stay on the option rows;
        // a single answer collapses to "<answer> · Change" once picked.
        var isMultiQ = !!part.multi;
        var chosenQ = selectionList(selections[thisQIdx]);
        if (interactive) {
          var captured = thisQIdx;
          if (!isMultiQ && chosenQ.length > 0) {
            elements.push(h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
              h('div', { className: 'planner-msg-question-label' }, part.question),
              h('div', { className: 'planner-msg-selected-answer' },
                h('span', null, chosenQ[0]),
                h('button', {
                  className: 'planner-msg-change-btn',
                  onClick: function() {
                    var copy = Object.assign({}, selections);
                    delete copy[captured];
                    setSelections(copy);
                  }
                }, 'Change')
              )
            ));
          } else {
            elements.push(h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
              h('div', { className: 'planner-msg-question-label' }, part.question),
              isMultiQ ? h('div', { className: 'planner-msg-question-hint' }, 'Select all that apply') : null,
              h('div', { className: 'planner-msg-options' },
                part.options.map(function(opt, oIdx) {
                  var isSel = isMultiQ && chosenQ.indexOf(opt) !== -1;
                  return h('button', {
                    key: oIdx,
                    className: 'planner-msg-option-btn' + (isSel ? ' planner-msg-option-selected' : ''),
                    onClick: function() {
                      var copy = Object.assign({}, selections);
                      if (!isMultiQ) {
                        copy[captured] = opt;
                      } else if (isSel) {
                        var left = chosenQ.filter(function(v) { return v !== opt; });
                        if (left.length) copy[captured] = left; else delete copy[captured];
                      } else {
                        copy[captured] = chosenQ.concat([opt]);
                      }
                      setSelections(copy);
                    }
                  }, opt);
                })
              )
            ));
          }
        } else {
          elements.push(h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
            h('div', { className: 'planner-msg-question-label' }, part.question),
            h('div', { className: 'planner-msg-options-static' },
              part.options.map(function(opt, oIdx) {
                return h('div', { key: oIdx, className: 'planner-msg-option-static' }, '• ' + opt);
              })
            )
          ));
        }
      } else if (part.type === 'input') {
        var thisQIdx2 = qIdx++;
        var current = selections[thisQIdx2] || '';
        if (interactive) {
          var captured2 = thisQIdx2;
          elements.push(h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
            h('div', { className: 'planner-msg-question-label' }, part.question),
            h('textarea', {
              className: 'planner-msg-input-textarea',
              placeholder: 'Type your answer...',
              value: current,
              rows: 2,
              onChange: function(e) {
                var copy = Object.assign({}, selections);
                copy[captured2] = e.target.value;
                setSelections(copy);
              }
            })
          ));
        }
      }
    });

    if (interactive) {
      var answered = Object.keys(selections).filter(function(k) { return selections[k] && String(selections[k]).trim(); }).length;
      if (answered > 0) {
        elements.push(h('div', { key: 'submit-row', className: 'planner-msg-submit-row' },
          h('button', {
            className: 'planner-msg-submit-btn',
            onClick: function() { submitSelections(questions); }
          }, 'Submit Answers')
        ));
      }
    }

    return h('div', { key: idx, className: 'planner-message planner-message-ai' },
      h('div', { className: 'planner-message-content' },
        h('div', { className: 'planner-message-sender' }, 'UI Planner'),
        h('div', { className: 'planner-message-bubble' }, elements)
      )
    );
  }

  // ── Minimized pill ────────────────────────────────────────────────────────
  if (minimized) {
    var pill = h('button', {
      className: 'planner-pill',
      type: 'button',
      onClick: function(e) { e.preventDefault(); e.stopPropagation(); setMinimized(false); }
    },
      h('span', { className: 'planner-pill-label' },
        uiPage ? 'UI page ready' : phase === 'probing' ? 'Probing...' : 'UI Planner')
    );
    if (typeof document !== 'undefined' && document.body && ReactDOM && ReactDOM.createPortal) {
      return ReactDOM.createPortal(pill, document.body);
    }
    return pill;
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return h('div', {
    className: 'planner-overlay',
    onMouseDown: function(e) {
      e.stopPropagation();
      if (e.target === e.currentTarget && !loading) onClose();
    }
  },
    h('div', {
      className: 'planner-modal',
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      // Header
      h('div', { className: 'planner-header' },
        h('div', { className: 'planner-header-left' },
          h('div', { className: 'planner-title' }, 'UI Planner'),
          h('span', { className: 'planner-badge' }, 'Beta')
        ),
        h('div', { className: 'planner-header-actions' },
          h('button', {
            className: 'planner-minimize-btn',
            onClick: function() { setMinimized(true); },
            title: 'Minimize'
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
            )
          ),
          h('button', {
            className: 'planner-close-btn',
            onClick: onClose,
            title: 'Close'
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
              h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
            )
          )
        )
      ),

      // Body
      h('div', { className: 'planner-body planner-body-with-plan' },
        // Left: chat
        h('div', { className: 'planner-chat' },
          h('div', { className: 'planner-messages' },
            messages.map(function(msg, idx) {
              var isUser = msg.role === 'user';
              var isLast = idx === messages.length - 1;
              if (!isUser) return renderAiMessage(msg, idx, isLast);
              return h('div', { key: idx, className: 'planner-message planner-message-user' },
                h('div', { className: 'planner-message-bubble' },
                  msg.attachment
                    ? h('img', {
                        src: msg.attachment,
                        alt: 'attached screenshot',
                        style: {
                          display: 'block',
                          maxWidth: '260px',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.25)',
                          marginBottom: '8px'
                        }
                      })
                    : null,
                  // UI-select selection label — shows what element this turn refined.
                  msg.selectionLabel
                    ? h('div', {
                        style: {
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px',
                          marginBottom: '8px',
                          background: 'rgba(255,255,255,0.18)',
                          border: '1px solid rgba(255,255,255,0.28)',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          color: '#ffffff'
                        }
                      },
                        h('svg', { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
                          h('path', { d: 'M3 3l7 18 2-8 8-2z' })
                        ),
                        '<' + msg.selectionLabel + '>'
                      )
                    : null,
                  (msg.content && msg.content !== '(screenshot)') ? h('div', null, msg.content) : null
                )
              );
            }),
            loading && streamingText
              ? h('div', { className: 'planner-message planner-message-ai' },
                  h('div', { className: 'planner-message-content' },
                    h('div', { className: 'planner-message-sender' }, 'UI Planner'),
                    h('div', { className: 'planner-message-bubble planner-message-streaming' },
                      formatText(streamingText),
                      h('span', { className: 'planner-stream-cursor' })
                    )
                  )
                )
              : null,
            (loading || probing) && !streamingText
              ? h('div', { className: 'planner-message planner-message-ai' },
                  h('div', { className: 'planner-message-content' },
                    h('div', { className: 'planner-message-sender' }, 'UI Planner'),
                    h('div', { className: 'planner-message-bubble planner-message-typing' },
                      h('span', { className: 'planner-typing-dot' }),
                      h('span', { className: 'planner-typing-dot' }),
                      h('span', { className: 'planner-typing-dot' }),
                      thinkingStatus
                        ? h('span', { className: 'planner-thinking-label' }, thinkingStatus)
                        : null
                    )
                  )
                )
              : null,
            error ? h('div', { className: 'planner-chat-error' }, error) : null,
            h('div', { ref: messagesEndRef })
          ),

          // Token usage strip — mirrors WorkflowPlannerModal layout.
          tokenUsage
            ? h('div', { className: 'planner-token-usage' },
                h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, className: 'planner-token-icon' },
                  h('circle', { cx: 12, cy: 12, r: 10 }),
                  h('path', { d: 'M12 6v6l4 2' })
                ),
                (function() {
                  var parts = [];
                  var p  = tokenUsage.planning;
                  var pr = tokenUsage.probing;
                  if (pr && (pr.input + pr.output) > 0) {
                    parts.push(h('span', { key: 'pr', className: 'planner-token-phase' },
                      h('span', { className: 'planner-token-label' }, 'Probing'),
                      h('span', { className: 'planner-token-value' }, formatTokenCount(pr.input + pr.output))
                    ));
                  }
                  if (p && (p.input + p.output) > 0) {
                    parts.push(h('span', { key: 'p', className: 'planner-token-phase' },
                      h('span', { className: 'planner-token-label' }, 'Planning'),
                      h('span', { className: 'planner-token-value' }, formatTokenCount(p.input + p.output))
                    ));
                  }
                  var total = (p ? p.input + p.output : 0) + (pr ? pr.input + pr.output : 0);
                  parts.push(h('span', { key: 't', className: 'planner-token-phase planner-token-total' },
                    h('span', { className: 'planner-token-label' }, 'Total'),
                    h('span', { className: 'planner-token-value' }, formatTokenCount(total))
                  ));
                  return parts;
                })()
              )
            : null,

          // Input row — enabled in pick & chat; disabled while probing or loading.
          h('div', { ref: inputContainerRef, className: 'planner-input-container', style: { position: 'relative' } },
            // @-mention workflow suggestions popover. Rendered via portal to
            // document.body so it escapes overflow:hidden on ancestor containers.
            mentionOpen && mentionFiltered.length && inputContainerRef.current
              ? ReactDOM.createPortal(
                  h('div', {
                    style: (function() {
                      var rect = inputContainerRef.current.getBoundingClientRect();
                      return {
                        position: 'fixed',
                        left: rect.left + 12 + 'px',
                        width: (rect.width - 24) + 'px',
                        bottom: (window.innerHeight - rect.top + 4) + 'px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 12px 28px -8px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.06)',
                        overflow: 'hidden',
                        zIndex: 10000,
                        maxHeight: '260px',
                        overflowY: 'auto'
                      };
                    })()
                  },
                    h('div', {
                      style: {
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#94a3b8',
                        borderBottom: '1px solid #f1f5f9',
                        background: '#f8fafc'
                      }
                    }, 'Mention a workflow'),
                    mentionFiltered.map(function(wf, idx) {
                      var active = idx === mention.highlight;
                      return h('button', {
                        key: wf.id,
                        type: 'button',
                        onMouseDown: function(e) {
                          // Prevent the textarea from losing focus before applyMention runs.
                          e.preventDefault();
                          applyMention(wf);
                        },
                        onMouseEnter: function() {
                          setMention(function(prev) { return Object.assign({}, prev, { highlight: idx }); });
                        },
                        style: {
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '2px',
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'left',
                          background: active ? '#eff6ff' : '#ffffff',
                          border: 'none',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: 'pointer'
                        }
                      },
                        h('div', {
                          style: { fontSize: '13px', fontWeight: 600, color: '#0f172a' }
                        }, wf.name || wf.id),
                        h('div', {
                          style: { fontSize: '11px', color: '#94a3b8', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }
                        }, wf.id)
                      );
                    })
                  ),
                  document.body
                )
              : null,
            // Selection chip — shown when the user picked a single element
            // via "UI select". The next chat send refines ONLY that element.
            selection
              ? h('div', {
                  style: {
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px', margin: '0 12px 6px 12px',
                    border: '1px solid #bae6fd', borderRadius: '10px',
                    background: '#f0f9ff'
                  }
                },
                  h('div', {
                    style: {
                      width: '32px', height: '32px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '6px',
                      background: '#0ea5e9', color: '#ffffff'
                    }
                  },
                    h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('path', { d: 'M9 11l3 3L22 4' }),
                      h('path', { d: 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' })
                    )
                  ),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: '12px', fontWeight: 600, color: '#0c4a6e' } },
                      'Element selected'),
                    h('div', {
                      style: {
                        fontSize: '11px', color: '#0369a1',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }
                    }, '<' + selection.label + '> · refine just this (saves tokens)')
                  ),
                  h('button', {
                    type: 'button',
                    onClick: handleSelectionRemove,
                    title: 'Clear selection',
                    style: {
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '4px', color: '#0369a1'
                    }
                  },
                    h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
                      h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
                    )
                  )
                )
              : null,
            // Attachment chip — shown when the user has captured a snapshot
            // of the live preview. The image will be sent to Sonnet vision
            // on the next chat turn. Token estimate makes the cost visible.
            attachment
              ? h('div', {
                  style: {
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px', margin: '0 12px 6px 12px',
                    border: '1px solid #e2e8f0', borderRadius: '10px',
                    background: '#f8fafc'
                  }
                },
                  h('img', {
                    src: attachment.dataUrl,
                    alt: 'attached preview snapshot',
                    style: {
                      width: '48px', height: '48px', objectFit: 'cover',
                      borderRadius: '6px', border: '1px solid #cbd5e1'
                    }
                  }),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: '12px', fontWeight: 600, color: '#0f172a' } },
                      'Preview snapshot attached'),
                    h('div', { style: { fontSize: '11px', color: '#64748b' } },
                      attachment.width + '\u00d7' + attachment.height +
                      ' \u00b7 \u2248' + formatTokenCount(attachment.tokens) + ' tokens'
                    )
                  ),
                  h('button', {
                    type: 'button',
                    onClick: handleAttachmentRemove,
                    title: 'Remove attachment',
                    style: {
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '4px', color: '#94a3b8'
                    }
                  },
                    h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
                      h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
                    )
                  )
                )
              : null,
            h('div', { className: 'planner-input-row' },
              h('textarea', {
                ref: inputRef,
                className: 'planner-input',
                placeholder: (loading || probing)
                  ? (probing ? 'Probing — type your next message, it\u2019ll send when ready...' : 'Generating — type your next message, it\u2019ll send when ready...')
                  : phase === 'pick'
                    ? ((singleWorkflowMode || focusWorkflowId)
                        ? 'Describe the UI you want...'
                        : (selectedIds.length
                            ? 'Describe the UI you want — sending will probe the selected workflows... (type @ to mention a workflow)'
                            : 'Pick at least one workflow on the right, then describe the UI you want...'))
                    : existingPage
                      ? 'What would you like to change? (Type @ to mention a workflow)'
                      : (uiPage ? 'Refine the page or ask for changes... (type @ to mention a workflow)' : 'Describe the UI you want... (type @ to mention a workflow)'),
                value: input,
                onChange: onInputChange,
                onKeyDown: onKeyDown,
                onKeyUp: onInputKeyUp,
                onClick: function(e) { refreshMentionFromTextarea(e.target.value, e.target.selectionStart); },
                onBlur: function() {
                  // Delay so a click on a suggestion still fires before the picker closes.
                  setTimeout(function() { closeMention(); }, 120);
                },
                // Stay enabled while loading/probing so the user can compose
                // (and queue) follow-up messages — actual send is gated below.
                disabled: false,
                rows: 2,
                spellCheck: false
              })
            ),
            h('div', { className: 'planner-input-toolbar', style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
              // Left side: capture tools (snapshot + UI select)
              h('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                // Snapshot preview button — captures the iframe to a base64 PNG.
                (function() {
                  var disabled = captureBusy || phase === 'probing' || loading || !uiPage || !uiPage.html;
                  return h('button', {
                    type: 'button',
                    className: 'planner-tool-btn' + (disabled ? ' planner-tool-btn-disabled' : ''),
                    onClick: handleScreenCapture,
                    disabled: disabled,
                    title: !uiPage || !uiPage.html
                      ? 'Generate a preview first, then snapshot it for the AI to analyze'
                      : 'Snapshot the live preview and attach it to the next message'
                  },
                    h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('rect', { x: 3, y: 5, width: 18, height: 14, rx: 2 }),
                      h('circle', { cx: 12, cy: 12, r: 3 })
                    ),
                    captureBusy ? 'Capturing...' : (attachment ? 'Recapture preview' : 'Snapshot preview')
                  );
                })(),

                // UI select button — click an element in the live preview
                // to refine ONLY that element on the next send. Massive token
                // savings vs. regenerating the whole page.
                (function() {
                  var disabled = phase === 'probing' || loading || !uiPage || !uiPage.html;
                  var active = selectMode;
                  return h('button', {
                    type: 'button',
                    className: 'planner-tool-btn' + (active ? ' planner-tool-btn-active' : '') + (disabled ? ' planner-tool-btn-disabled' : ''),
                    onClick: handleToggleSelectMode,
                    disabled: disabled,
                    title: !uiPage || !uiPage.html
                      ? 'Generate a preview first, then click an element to refine just that part'
                      : (active
                          ? 'Click an element in the preview to select it (Esc to cancel)'
                          : 'Pick a single element to refine — saves tokens by only regenerating that element')
                  },
                    h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('path', { d: 'M3 3l7 18 2-8 8-2z' })
                    ),
                    active ? 'Click an element...' : (selection ? 'Pick another' : 'UI select')
                  );
                })()
              ),
              h('button', {
                className: 'planner-send-btn' + (loading ? ' planner-send-btn-loading' : ''),
                onClick: sendMessage,
                // Allow sending while loading/probing — sendMessage queues it
                // and fires once the current operation finishes.
                disabled: (!input.trim() && !attachment && !selection)
                  || (phase === 'pick' && !selectedIds.length && !singleWorkflowMode && !focusWorkflowId),
                title: (loading || probing) ? 'Will send when current response finishes' : undefined
              },
                loading
                  ? h('span', { className: 'planner-send-spinner' })
                  : h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
                      h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
                    )
              )
            )
          )
        ),

        // Right: phase-specific panel
        h('div', { className: 'planner-preview-panel' },
          phase === 'pick'
            ? ((singleWorkflowMode || focusWorkflowId)
                ? h(PreviewPanel, { uiPage: null })
                : h(WorkflowPickerPanel, {
                    workflows: workflows,
                    selectedIds: selectedIds,
                    onToggle: toggleSelection,
                    disabled: false
                  }))
            : phase === 'probing'
              ? h(ProbeProgressPanel, { probeEvents: probeEvents })
              : uiPage
                ? h('div', { style: { display: 'flex', flexDirection: 'column', height: '100%' } },
                    h('div', { className: 'planner-plan-header' },
                      h('div', { className: 'planner-plan-title' }, 'Live Preview'),
                      h('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        h('button', {
                          onClick: undoUiPage,
                          disabled: !canUndo,
                          title: 'Undo',
                          style: { padding: '4px 8px', fontSize: '13px', background: 'none', border: 'none', cursor: canUndo ? 'pointer' : 'default', opacity: canUndo ? 1 : 0.35, color: 'inherit' }
                        }, '\u21A9'),
                        h('button', {
                          onClick: redoUiPage,
                          disabled: !canRedo,
                          title: 'Redo',
                          style: { padding: '4px 8px', fontSize: '13px', background: 'none', border: 'none', cursor: canRedo ? 'pointer' : 'default', opacity: canRedo ? 1 : 0.35, color: 'inherit' }
                        }, '\u21AA'),
                        h('button', {
                          className: 'planner-build-btn',
                          onClick: handleSavePage,
                          disabled: saving,
                          style: { padding: '6px 14px', fontSize: '12px' }
                        }, saving
                          ? (existingPage ? 'Updating...' : 'Saving...')
                          : (existingPage ? 'Update Page' : 'Save Page'))
                      )
                    ),
                    h('div', { style: { flex: 1, overflowY: 'auto', background: '#f8fafc' } },
                      h(PreviewPanel, { uiPage: uiPage })
                    )
                  )
                : h(PreviewPanel, { uiPage: null })
        )
      )
    ),

    // Crop overlay — only rendered after a successful preview snapshot.
    // Lets the user select a region; the cropped result becomes the
    // attachment chip below the input.
    captureRaw
      ? h(CropOverlay, {
          imageDataUrl: captureRaw,
          onConfirm: handleCropConfirm,
          onCancel: handleCropCancel
        })
      : null
  );
}

module.exports = UIPlannerModal;
